import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Menu, X, ChevronDown, Building2, Check } from "lucide-react";
import { DoxMascot } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { useBusiness } from "@/context/BusinessContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

export default function DashShell({ nav, active, onNavigate, children, title, headerRight, doxStatus }) {
  const { user, logout } = useAuth();
  const { businesses, active: activeBiz, switchBusiness } = useBusiness();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  const sideContent = (
    <>
      <button onClick={() => navigate("/")} className="px-1 mb-10 block group" data-testid="sidebar-logo">
        <span className="inline-flex items-baseline gap-0 font-display font-black tracking-tight text-2xl">
          <span className="text-white">ez</span>
          <span className="text-[#1e73e8]">dox</span>
          <span className="text-white">ai</span>
        </span>
      </button>
      <nav className="flex-1 space-y-1">
        {nav.map((item) => (
          <button
            key={item.key}
            data-testid={`dash-nav-${item.key}`}
            onClick={() => { onNavigate(item.key); setOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-3 min-h-[48px] rounded-xl text-sm font-medium transition-colors ${
              active === item.key ? "bg-dox-orange/15 text-dox-orange" : "text-white/55 hover:text-white hover:bg-white/5"
            }`}
          >
            <item.icon size={18} />
            {item.label}
          </button>
        ))}
      </nav>
      <div className="mt-auto pt-4 border-t border-white/10">
        <div className="flex items-center gap-3 px-2 mb-3">
          <div className="h-9 w-9 rounded-full bg-gradient-to-br from-dox-orange to-dox-purple flex items-center justify-center text-sm font-bold shrink-0">
            {(user?.name || "U").slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate">{user?.name}</div>
            <div className="text-xs text-white/40 truncate">{user?.email}</div>
          </div>
        </div>
        <button
          data-testid="dash-logout"
          onClick={doLogout}
          className="w-full flex items-center gap-2 px-3 py-2.5 min-h-[44px] rounded-xl text-sm text-white/55 hover:text-white hover:bg-white/5 transition-colors"
        >
          <LogOut size={16} /> Log out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-ink text-white flex dash-stage">
      <div className="noise-overlay" />
      {/* desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col p-5 border-r border-white/10 glass-strong relative z-10">
        {sideContent}
      </aside>

      {/* mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 flex flex-col p-5 glass-strong border-r border-white/10">
            {sideContent}
          </aside>
        </div>
      )}

      <div className="flex-1 min-w-0 relative z-[2]">
        <header className="sticky top-0 z-30 glass-strong border-b border-white/10 px-4 sm:px-8 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <button
              className="lg:hidden text-white p-2 -ml-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
              onClick={() => setOpen(true)}
              data-testid="dash-mobile-menu"
              aria-label="Open menu"
            >
              <Menu size={22} />
            </button>
            <h1 className="font-display font-bold text-base sm:text-xl truncate">{title}</h1>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            {businesses && businesses.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="business-switcher" className="hidden sm:flex items-center gap-2 glass rounded-full pl-3 pr-2.5 py-1.5 min-h-[40px] text-sm text-white/80 hover:text-white max-w-[220px]">
                    <Building2 size={14} className="text-dox-orange shrink-0" />
                    <span className="truncate">{activeBiz?.name || "Select business"}</span>
                    <ChevronDown size={14} className="shrink-0 opacity-60" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-elevated border-white/10 text-white w-60">
                  {businesses.map((b) => (
                    <DropdownMenuItem key={b.id} data-testid={`switch-biz-${b.id}`} onClick={() => b.id !== activeBiz?.id && switchBusiness(b.id)} className="cursor-pointer focus:bg-white/10 focus:text-white flex items-center justify-between">
                      <span className="truncate"><span className="block">{b.name}</span><span className="text-xs text-white/40">{b.industry} · {b.role}</span></span>
                      {b.id === activeBiz?.id && <Check size={14} className="text-dox-orange shrink-0" />}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {headerRight}
            <DoxStatusBadge status={doxStatus} />
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-7xl">{children}</main>
      </div>
    </div>
  );
}

export { X, DoxMascot };

/**
 * Live "Dox is Working" employee-style status pill.
 * Cycles between: reviewing → approval → preparing → submitted → on the clock.
 * The green dot pulses when Dox is actively doing something.
 */
function DoxStatusBadge({ status }) {
  const s = status || { key: "onclock", label: "On the clock", tone: "emerald", pulse: false };
  const toneMap = {
    emerald: "bg-emerald-400",
    amber: "bg-amber-400",
    rose: "bg-rose-400",
    sky: "bg-sky-400",
    violet: "bg-violet-400",
  };
  const dot = toneMap[s.tone] || toneMap.emerald;
  return (
    <div
      data-testid={`dox-status-${s.key}`}
      className="hidden sm:inline-flex items-center gap-2 rounded-full glass-strong border border-white/10 px-3 py-1.5 transition-all"
    >
      <span className="relative flex h-2 w-2">
        {s.pulse && <span className={`absolute inline-flex h-full w-full animate-ping rounded-full ${dot} opacity-60`} />}
        <span className={`relative inline-flex h-2 w-2 rounded-full ${dot}`} />
      </span>
      <span className="text-xs text-white/80 font-medium">Dox is <span className="text-white">{s.label}</span></span>
    </div>
  );
}
